import React, { useContext } from "react";
import BreakpointContext from "../../contexts/breakpointContext";
import DraftStateContext from "../../contexts/draftStateContext";
import useDraftOnClock from "../../hooks/queryResults/draft/useDraftOnClock";
import useDraftPicks from "../../hooks/queryResults/draft/useDraftPicks";
import useDraftSettings from "../../hooks/queryResults/draft/useDraftSettings";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import useTeams from "../../hooks/queryResults/useTeams";
import useCurrentUser from "../../hooks/useCurrentUser";
import Util from "../../util/util";
import Loading from "../ui/Loading";
import TeamLogoWithRosterLink from "./TeamLogoWithRosterLink";

export default function OnClock() {
  const breakpoints = useContext(BreakpointContext);
  const draftState = useContext(DraftStateContext);
  const currentUser = useCurrentUser();
  const {
    hasStarted,
    teamIdOnClock,
    teamIdUpNext,
    timeIsRunningOut,
    pickNumOnClock,
  } = draftState.info;
  const { draftSettingsData, draftSettingsQuery } = useDraftSettings();
  const { draftPicksData, draftPicksQuery } = useDraftPicks();
  const { teams, teamsQuery } = useTeams();

  const { formatted: clockFormatted } = useDraftOnClock();

  const isLoading = useIsLoading(
    draftSettingsData,
    draftSettingsQuery,
    draftPicksData,
    draftPicksQuery,
    teams,
    teamsQuery
  );
  if (isLoading) return <Loading />;

  const teamOnClock = teamIdOnClock ? teams.getTeamById(teamIdOnClock) : null;
  const userHasDraftManagerRole = Util.hasDraftManagerRole(
    currentUser,
    teamOnClock
  );

  const { numTeams } = draftSettingsData.settings;

  let teamIds;
  if (!draftState.info.hasStarted || !draftSettingsData.settings.pickSpotsFirst)
    teamIds = draftSettingsData.settings.draftOrder;
  else
    teamIds = draftPicksData.picks.slice(0, numTeams).map((pick) => pick.team);

  let imgSize = breakpoints.md ? 44 : 22;

  if (breakpoints.xs && !breakpoints.sm) {
    const width = 5;
    if (!teamIdOnClock) {
      teamIds = teamIds.slice(0, width + 1);
    } else {
      const indexOnClock = teamIds.indexOf(teamIdOnClock);

      let start = Math.min(Math.max(indexOnClock - 2, 0), numTeams - width);
      let end = start + width;
      teamIds = teamIds.slice(start, end);
    }
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "0.5rem",
      }}
    >
      {teamIds.map((teamId, i) => {
        let team = null;
        if (teamId) team = teams.getTeamById(teamId);
        let isOnClock = teamId === teamIdOnClock;
        let isUpNext = teamId === teamIdUpNext;

        let teamImgSize = imgSize;
        if (hasStarted) {
          if (isUpNext) teamImgSize = imgSize * 1.25;
          if (isOnClock) teamImgSize = imgSize * 1.5;
        }

        let backgroundColor = "white";
        let color = "black";
        let clock = null;

        if (isOnClock) {
          if (userHasDraftManagerRole) {
            backgroundColor = timeIsRunningOut ? "red" : "#00ab00";
          } else {
            backgroundColor = "#0078f8";
          }

          color = "white";

          clock = draftState.info.isPaused ? (
            <div style={{ fontSize: "1rem" }}>PAUSED</div>
          ) : (
            <div style={{ fontSize: "1.5rem" }}>{clockFormatted}</div>
          );
        }

        let pickNumOrNext = null;
        if (!hasStarted) {
          pickNumOrNext = i + 1;
        } else if (isUpNext && !isOnClock) {
          pickNumOrNext = "Up Next";
        } else if (isOnClock) {
          const roundNum = Util.getRoundFromPickNum(pickNumOnClock, numTeams);
          pickNumOrNext = `Rd ${roundNum} Pk ${pickNumOnClock}`;
        }

        return (
          <div
            style={{
              textAlign: "center",
              color,
              backgroundColor,
              padding: "0.25rem",
            }}
            key={i}
          >
            <TeamLogoWithRosterLink
              team={team}
              size={breakpoints.md ? "lg" : "sm"}
              styleProps={{
                width: teamImgSize + "px",
                height: teamImgSize + "px",
                imageRendering: "crisp-edges",
                margin: 0,
                padding: 0,
                cursor: "pointer",
              }}
            />
            <p style={{ margin: "0.5rem 0 0.25rem", opacity: 0.75 }}>
              {pickNumOrNext}
            </p>
            {clock}
          </div>
        );
      })}
    </div>
  );
}
